import { useEffect, useRef, useState } from "react"
import type { RefObject } from "react"

interface UseIsCut<T extends HTMLElement> {
  ref: RefObject<T | null>
  isCut: boolean
}

/**
 * Whether the text in `ref` no longer fits its box, for a tooltip that only shows the
 * full text when it was cut. Measured again whenever the box resizes or its text changes.
 */
const useIsCut = <T extends HTMLElement>(
  content: unknown,
  enabled = true,
): UseIsCut<T> => {
  const ref = useRef<T>(null)
  const [isCut, setIsCut] = useState(false)
  // Only text can change what is cut without resizing the box; an element is a new
  // object on every render and would rebuild the observer each time.
  const text = typeof content === "string" || typeof content === "number" ? content : null

  useEffect(() => {
    const element = ref.current
    if (!enabled || !element || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(() =>
      setIsCut(
        element.scrollHeight > element.clientHeight ||
          element.scrollWidth > element.clientWidth,
      ),
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [enabled, text])

  return { ref, isCut }
}

export default useIsCut
